import React, { useContext, useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import Grid from '@mui/material/Unstable_Grid2';
import { AuthContext } from '../Contexts/AuthContext';
import { apiFetch } from '../Helpers/api';

const PROFILE_TABS = ['library', 'messages', 'commissions', 'agreement'];
const COMMISSION_TERMS_VERSION = '2026-07-09';

function cents(amount) {
  return `$${((Number(amount) || 0) / 100).toFixed(2)}`;
}

function dateLabel(value) {
  if (!value) {
    return 'not recorded';
  }

  return new Date(value.replace(' ', 'T')).toLocaleDateString();
}

export function ProfileConsole({ open, mode = 'login', resetToken = '', onClose, changePage }) {
  const auth = useContext(AuthContext);
  const [authMode, setAuthMode] = useState('login');
  const [activeTab, setActiveTab] = useState('library');
  const [identifier, setIdentifier] = useState('');
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [resetPassword, setResetPassword] = useState('');
  const [resetPasswordConfirm, setResetPasswordConfirm] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [authError, setAuthError] = useState('');
  const [authWorking, setAuthWorking] = useState(false);
  const [entitlements, setEntitlements] = useState([]);
  const [entitlementsLoading, setEntitlementsLoading] = useState(false);
  const [entitlementsError, setEntitlementsError] = useState('');
  const [commissionData, setCommissionData] = useState(null);
  const [commissionsLoading, setCommissionsLoading] = useState(false);
  const [commissionsError, setCommissionsError] = useState('');
  const [termsWorking, setTermsWorking] = useState(false);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesError, setMessagesError] = useState('');

  function clearAuthForm() {
    setIdentifier('');
    setUsername('');
    setDisplayName('');
    setEmail('');
    setPassword('');
    setResetPassword('');
    setResetPasswordConfirm('');
    setResetMessage('');
    setAuthError('');
  }

  useEffect(() => {
    if (!open) {
      return;
    }

    if (mode === 'reset-password') {
      setAuthMode('reset-password');
    } else if (mode === 'forgot-password') {
      setAuthMode('forgot-password');
    } else if (mode === 'register') {
      setAuthMode('register');
    } else {
      setAuthMode('login');
    }

    if (mode === 'messages') {
      setActiveTab('messages');
    } else if (mode === 'earnings' || mode === 'commissions') {
      setActiveTab('commissions');
    } else if (mode === 'agreement') {
      setActiveTab('agreement');
    } else {
      setActiveTab('library');
    }

    clearAuthForm();
  }, [open, mode]);

  useEffect(() => {
    if (!open || !auth.user) {
      setEntitlements([]);
      setEntitlementsError('');
      return;
    }

    setEntitlementsLoading(true);
    setEntitlementsError('');

    apiFetch('entitlements.php')
      .then((res) => res.json().then((json) => ({ res, json })))
      .then(({ res, json }) => {
        if (!res.ok || json.error) {
          throw new Error(json.error || 'Could not load purchases.');
        }

        setEntitlements(json.data?.entitlements || []);
      })
      .catch((error) => {
        setEntitlements([]);
        setEntitlementsError(error.message || 'Could not load purchases.');
      })
      .finally(() => setEntitlementsLoading(false));
  }, [open, auth.user]);

  useEffect(() => {
    if (!open || !auth.user) {
      setMessages([]);
      setMessagesError('');
      return;
    }

    loadMessages();
  }, [open, auth.user]);

  useEffect(() => {
    if (!open || !auth.user) {
      setCommissionData(null);
      setCommissionsError('');
      return;
    }

    loadCommissions();
  }, [open, auth.user]);

  async function loadCommissions() {
    setCommissionsLoading(true);
    setCommissionsError('');

    try {
      const res = await apiFetch('commissions.php');
      const json = await res.json();

      if (!res.ok || json.error) {
        throw new Error(json.error || 'Could not load commissions.');
      }

      setCommissionData(json.data);
    } catch (error) {
      setCommissionData(null);
      setCommissionsError(error.message || 'Could not load commissions.');
    } finally {
      setCommissionsLoading(false);
    }
  }

  async function loadMessages() {
    setMessagesLoading(true);
    setMessagesError('');

    try {
      const res = await apiFetch('usermessages.php');
      const json = await res.json();

      if (!res.ok || json.error) {
        throw new Error(json.error || 'Could not load messages.');
      }

      setMessages(json.data?.messages || []);
    } catch (error) {
      setMessages([]);
      setMessagesError(error.message || 'Could not load messages.');
    } finally {
      setMessagesLoading(false);
    }
  }

  async function markMessageRead(message) {
    if (message.read_at) {
      return;
    }

    try {
      const res = await apiFetch('usermessages.php', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message_id: message.message_id }),
      });
      const json = await res.json();

      if (!res.ok || json.error) {
        throw new Error(json.error || 'Could not mark message read.');
      }

      setMessages((currentMessages) => currentMessages.map((currentMessage) => (
        currentMessage.message_id === message.message_id
          ? { ...currentMessage, read_at: new Date().toISOString() }
          : currentMessage
      )));
    } catch (error) {
      setMessagesError(error.message || 'Could not mark message read.');
    }
  }

  async function acceptTerms() {
    setTermsWorking(true);
    setCommissionsError('');

    try {
      const res = await apiFetch('commissionterms.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ version: COMMISSION_TERMS_VERSION }),
      });
      const json = await res.json();

      if (!res.ok || json.error) {
        throw new Error(json.error || 'Could not accept commission terms.');
      }

      setCommissionData(json.data);
    } catch (error) {
      setCommissionsError(error.message || 'Could not accept commission terms.');
    } finally {
      setTermsWorking(false);
    }
  }

  async function submitAuth(event) {
    event.preventDefault();
    setAuthWorking(true);
    setAuthError('');

    try {
      if (authMode === 'login') {
        await auth.login({ identifier, password });
      } else {
        await auth.register({
          username,
          display_name: displayName || username,
          email,
          password,
        });
      }

      clearAuthForm();
      setActiveTab('library');
    } catch (error) {
      setAuthError(error.message);
    } finally {
      setAuthWorking(false);
    }
  }

  async function submitPasswordResetRequest(event) {
    event.preventDefault();
    setAuthWorking(true);
    setAuthError('');
    setResetMessage('');

    try {
      const res = await apiFetch('requestpasswordreset.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier }),
      });
      const json = await res.json();

      if (!res.ok || json.error) {
        throw new Error(json.error || 'Could not request password reset.');
      }

      setResetMessage(json.data?.message || 'If an account exists for that login, reset instructions have been sent.');
    } catch (error) {
      setAuthError(error.message);
    } finally {
      setAuthWorking(false);
    }
  }

  async function submitPasswordReset(event) {
    event.preventDefault();
    setAuthWorking(true);
    setAuthError('');
    setResetMessage('');

    try {
      if (resetPassword !== resetPasswordConfirm) {
        throw new Error('Passwords do not match.');
      }

      const res = await apiFetch('resetpassword.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: resetToken, password: resetPassword }),
      });
      const json = await res.json();

      if (!res.ok || json.error) {
        throw new Error(json.error || 'Could not reset password.');
      }

      auth.setAuthenticatedUser(json.data?.user || null);
      setResetPassword('');
      setResetPasswordConfirm('');
      setResetMessage('password updated');
      setActiveTab('library');

      if (window.history?.replaceState) {
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    } catch (error) {
      setAuthError(error.message);
    } finally {
      setAuthWorking(false);
    }
  }

  async function logout() {
    setAuthWorking(true);
    setAuthError('');

    try {
      await auth.logout();
      onClose();
    } catch (error) {
      setAuthError(error.message);
    } finally {
      setAuthWorking(false);
    }
  }

  function openEntitlement(entitlement) {
    if (entitlement.documentation_codebase_id) {
      changePage('documentation', entitlement.documentation_codebase_id);
      onClose();
    }
  }

  function openMessageCodebase(message) {
    markMessageRead(message);
    if (message.codebase_id) {
      changePage('documentation', message.codebase_id);
      onClose();
    }
  }

  function renderLogin() {
    if (authMode === 'forgot-password') {
      return (
        <form className="auth-panel" onSubmit={submitPasswordResetRequest}>
          <h2>reset password</h2>
          <label className="auth-field">
            <span className="green-code">email or username</span>
            <input value={identifier} onChange={(event) => setIdentifier(event.target.value)} autoComplete="username" />
          </label>
          {authError && <div className="auth-error">{authError}</div>}
          {resetMessage && <div className="cart-checkout-message">{resetMessage}</div>}
          <button className="cli-button" type="submit" disabled={authWorking}>
            {authWorking ? 'sending...' : 'send reset link'}
          </button>
          <button
            className="cli-button-white"
            type="button"
            onClick={() => {
              setAuthMode('login');
              setAuthError('');
              setResetMessage('');
            }}
          >
            back to login
          </button>
        </form>
      );
    }

    if (authMode === 'reset-password') {
      return (
        <form className="auth-panel" onSubmit={submitPasswordReset}>
          <h2>set new password</h2>
          <label className="auth-field">
            <span className="green-code">new password</span>
            <input value={resetPassword} onChange={(event) => setResetPassword(event.target.value)} type="password" autoComplete="new-password" />
          </label>
          <label className="auth-field">
            <span className="green-code">confirm password</span>
            <input value={resetPasswordConfirm} onChange={(event) => setResetPasswordConfirm(event.target.value)} type="password" autoComplete="new-password" />
          </label>
          {authError && <div className="auth-error">{authError}</div>}
          {resetMessage && <div className="cart-checkout-message">{resetMessage}</div>}
          <button className="cli-button" type="submit" disabled={authWorking}>
            {authWorking ? 'saving...' : 'reset password'}
          </button>
          <button
            className="cli-button-white"
            type="button"
            onClick={() => {
              setAuthMode('login');
              setAuthError('');
              setResetMessage('');
            }}
          >
            back to login
          </button>
        </form>
      );
    }

    return (
      <form className="auth-panel" onSubmit={submitAuth}>
        <h2>{authMode === 'login' ? 'log in' : 'create account'}</h2>
        {authMode === 'login' ? (
          <label className="auth-field">
            <span className="green-code">email or username</span>
            <input value={identifier} onChange={(event) => setIdentifier(event.target.value)} autoComplete="username" />
          </label>
        ) : (
          <>
            <label className="auth-field">
              <span className="green-code">username</span>
              <input value={username} onChange={(event) => setUsername(event.target.value)} autoComplete="username" />
            </label>
            <label className="auth-field">
              <span className="green-code">display name</span>
              <input value={displayName} onChange={(event) => setDisplayName(event.target.value)} autoComplete="name" />
            </label>
            <label className="auth-field">
              <span className="green-code">email</span>
              <input value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="email" />
            </label>
          </>
        )}
        <label className="auth-field">
          <span className="green-code">password</span>
          <input value={password} onChange={(event) => setPassword(event.target.value)} type="password" autoComplete={authMode === 'login' ? 'current-password' : 'new-password'} />
        </label>
        {authError && <div className="auth-error">{authError}</div>}
        <button className="cli-button" type="submit" disabled={authWorking}>
          {authWorking ? 'working...' : authMode === 'login' ? 'log in' : 'create account'}
        </button>
        {authMode === 'login' && (
          <button
            className="cli-button-white"
            type="button"
            onClick={() => {
              setAuthMode('forgot-password');
              setAuthError('');
              setResetMessage('');
            }}
          >
            forgot password?
          </button>
        )}
        <button
          className="cli-button-white"
          type="button"
          onClick={() => {
            setAuthMode(authMode === 'login' ? 'register' : 'login');
            setAuthError('');
          }}
        >
          {authMode === 'login' ? 'create account' : 'back to login'}
        </button>
      </form>
    );
  }

  function renderLibrary() {
    return (
      <div className="profile-panel">
        <div className="profile-command-line">
          <span className="green-code">library&gt;</span>
          <span className="white-code">list --entitlements</span>
        </div>
        {entitlementsLoading && <div className="grey-code">loading purchases...</div>}
        {entitlementsError && <div className="auth-error">{entitlementsError}</div>}
        {!entitlementsLoading && !entitlementsError && entitlements.length === 0 && (
          <div className="profile-empty">no purchases found</div>
        )}
        {!entitlementsLoading && !entitlementsError && entitlements.length > 0 && (
          <div className="profile-entitlements">
            {entitlements.map((entitlement, index) => (
              <button
                type="button"
                className={entitlement.documentation_codebase_id ? 'profile-entitlement-line' : 'profile-entitlement-line profile-entitlement-static'}
                onClick={() => openEntitlement(entitlement)}
                disabled={!entitlement.documentation_codebase_id}
                key={entitlement.entitlement_id}
              >
                <span className="cli-result-index">{String(index + 1).padStart(2, '0')}</span>
                <span className="profile-entitlement-main">
                  <span className="profile-entitlement-name">{entitlement.name}</span>
                  <span className="component-tag">{entitlement.product_type}</span>
                  {entitlement.quantity > 1 && <span className="component-tag">qty {entitlement.quantity}</span>}
                </span>
                <span className="cli-result-description">
                  {entitlement.product_type === 'codebase'
                    ? (entitlement.designer ? `documentation by ${entitlement.designer}` : 'documentation access')
                    : `${entitlement.flavor || 'bootkit'}${entitlement.codebase_credits ? ` / ${entitlement.codebase_credits} codebase credits` : ''}`}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  function renderCommissions() {
    const summary = commissionData?.summary || {};
    const codebases = commissionData?.codebases || [];
    const ledger = commissionData?.ledger || [];

    return (
      <div className="profile-panel">
        <div className="profile-command-line">
          <span className="green-code">commissions&gt;</span>
          <span className="white-code">report --commissions</span>
        </div>
        {commissionsLoading && <div className="grey-code">loading commission ledger...</div>}
        {commissionsError && <div className="auth-error">{commissionsError}</div>}
        {!commissionsLoading && !commissionsError && (
          <>
            <div className="profile-summary-grid">
              <div><span className="grey-code">copies sold</span><strong>{summary.copies_sold || 0}</strong></div>
              <div><span className="grey-code">owed</span><strong>{cents(summary.owed_cents)}</strong></div>
              <div><span className="grey-code">paid</span><strong>{cents(summary.paid_cents)}</strong></div>
              <div><span className="grey-code">pending terms</span><strong>{cents(summary.pending_terms_cents)}</strong></div>
            </div>
            {codebases.length === 0 ? (
              <div className="profile-empty">no developer codebases found</div>
            ) : (
              <div className="profile-table">
                <div className="profile-table-row profile-table-head">
                  <span>codebase</span><span>status</span><span>sold</span><span>owed</span><span>paid</span>
                </div>
                {codebases.map((codebase) => (
                  <div className="profile-table-row" key={codebase.codebase_id}>
                    <span>{codebase.name}</span>
                    <span>{codebase.status}</span>
                    <span>{codebase.copies_sold}</span>
                    <span>{cents(codebase.owed_cents)}</span>
                    <span>{cents(codebase.paid_cents)}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="profile-section-heading">
              <span className="green-code">ledger&gt;</span>
              <span className="white-code">tail --recent</span>
            </div>
            {ledger.length === 0 ? (
              <div className="profile-empty">no commissionable sales recorded</div>
            ) : (
              <div className="profile-ledger">
                {ledger.map((entry, index) => (
                  <div className="profile-ledger-line" key={entry.commission_id}>
                    <span className="cli-result-index">{String(index + 1).padStart(2, '0')}</span>
                    <span>
                      <strong>{entry.codebase_name}</strong>
                      <span className="cli-result-description">order #{entry.order_id} / {dateLabel(entry.created_at)} / qty {entry.quantity}</span>
                    </span>
                    <span className={`component-tag commission-status-${entry.status}`}>{entry.status}</span>
                    <span>{cents(entry.amount_cents)}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  function renderMessages() {
    return (
      <div className="profile-panel">
        <div className="profile-command-line">
          <span className="green-code">messages&gt;</span>
          <span className="white-code">inbox --publication-feedback</span>
        </div>
        {messagesLoading && <div className="grey-code">loading messages...</div>}
        {messagesError && <div className="auth-error">{messagesError}</div>}
        {!messagesLoading && !messagesError && messages.length === 0 && (
          <div className="profile-empty">no messages found</div>
        )}
        {!messagesLoading && messages.length > 0 && (
          <div className="profile-messages">
            {messages.map((message, index) => (
              <div className={message.read_at ? 'profile-message' : 'profile-message profile-message-unread'} key={message.message_id}>
                <div className="profile-message-topline">
                  <span className="cli-result-index">{String(index + 1).padStart(2, '0')}</span>
                  <span className="profile-message-subject">{message.subject}</span>
                  {!message.read_at && <span className="component-tag">new</span>}
                </div>
                <div className="cli-result-description">
                  {message.codebase_name ? `${message.codebase_name} / ` : ''}
                  {dateLabel(message.created_at)} / from {message.sender_display_name || message.sender_username || 'admin'}
                </div>
                <div className="profile-message-body">{message.body}</div>
                <div className="profile-message-actions">
                  {message.codebase_id && (
                    <button className="cli-button" onClick={() => openMessageCodebase(message)}>
                      open codebase
                    </button>
                  )}
                  {!message.read_at && (
                    <button className="cli-button-white" onClick={() => markMessageRead(message)}>
                      mark read
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  function renderAgreement() {
    const agreement = commissionData?.agreement || {};
    const accepted = Boolean(agreement.accepted_at);

    return (
      <div className="profile-panel">
        <div className="profile-command-line">
          <span className="green-code">agreement&gt;</span>
          <span className="white-code">cat commission_terms.md</span>
        </div>
        {commissionsError && <div className="auth-error">{commissionsError}</div>}
        <div className={accepted ? 'profile-agreement-status profile-agreement-ok' : 'profile-agreement-status'}>
          {accepted
            ? `accepted ${dateLabel(agreement.accepted_at)} / version ${agreement.version}`
            : 'not yet accepted'}
        </div>
        <div className="profile-terms">
          <p><strong>Malware Product Commission Agreement</strong></p>
          <p>Developers may earn commissions when eligible Codebases they own are sold through the Malware marketplace.</p>
          <p>Commissionable sales are tracked in the site ledger after payment is confirmed. Refunded or voided sales may be reversed before payout.</p>
          <p>Current commission schedule: $2.00 per sold Codebase copy, unless a separate written agreement applies.</p>
          <p>Payout timing, method, tax documentation, and marketplace eligibility may be reviewed before payment is issued.</p>
        </div>
        <button className="cli-button" onClick={acceptTerms} disabled={termsWorking || accepted}>
          {termsWorking ? 'recording acceptance...' : accepted ? 'agreement accepted' : 'accept commission agreement'}
        </button>
      </div>
    );
  }

  return (
    <Dialog
      open={open}
      maxWidth={auth.user ? 'md' : 'xs'}
      fullWidth={true}
      onClose={onClose}
      PaperProps={{ sx: { bgcolor: 'black', border: 1, borderColor: 'white', padding: 3, color: 'white' } }}
    >
      {auth.user ? (
        <div className="auth-panel profile-console">
          <div className="profile-console-header">
            <div>
              <h2>profile</h2>
              <div className="white-code">{auth.user.display_name || auth.user.username}</div>
              <div className="grey-code">@{auth.user.username}</div>
            </div>
            <button className="close-button profile-close-button" onClick={onClose} type="button">x</button>
          </div>
          <Grid container spacing={2}>
            <Grid xs={12} md={3}>
              <nav className="profile-tabs" aria-label="profile sections">
                {PROFILE_TABS.map((tab) => (
                  <button
                    type="button"
                    className={activeTab === tab ? 'profile-tab profile-tab-active' : 'profile-tab'}
                    onClick={() => setActiveTab(tab)}
                    key={tab}
                  >
                    {tab}
                  </button>
                ))}
              </nav>
              <button className="cli-button profile-logout-button" onClick={logout} disabled={authWorking}>
                {authWorking ? 'logging out...' : 'log out'}
              </button>
            </Grid>
            <Grid xs={12} md={9}>
              {activeTab === 'library' && renderLibrary()}
              {activeTab === 'messages' && renderMessages()}
              {activeTab === 'commissions' && renderCommissions()}
              {activeTab === 'agreement' && renderAgreement()}
            </Grid>
          </Grid>
        </div>
      ) : renderLogin()}
    </Dialog>
  );
}
